import { inject, Injectable } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { KeyValuePair } from "@core/models/dyna.model";
import { Modules } from "@urls";
import { AlertService } from "./alerts.service";
import { ApiResponse, eMessageType, StatusFlags } from "./data.model";
import { DataService } from "./data.service";
import { ActionEvent, ActionRequest, FieldMapping, FormConfig, FormFields, OptionsTransfer, PageConfig, ValidatorNames } from "./gen.model";
import { Subject } from "rxjs";

@Injectable()
export class CRUDService {
  public alertService = inject(AlertService);
  public dataService = inject(DataService);
  public formBuilder = inject(FormBuilder);

  public isFormVisible = false;
  public isFormSubmitted = false;
  public pageConfig!: PageConfig;
  public dataForm: FormGroup = new FormGroup({});
  public options: OptionsTransfer[] = [];
  public actionSubject = new Subject<ActionRequest>();

  public loadData(pageConfig: PageConfig) {
    this.pageConfig = pageConfig;
    this.prepareOptions();
    this.prepareTable();
    this.prepareForm();
  }

  private prepareTable() {
    if (!this.pageConfig) { return; }
    if (!this.pageConfig.stopDefaultGet) {
      this.dataService.getData(`${Modules.Base}${this.pageConfig.apiUrl}`).then((response: any) => {
        if (response) {
          if (this.pageConfig.tableConfig) { this.pageConfig.tableConfig = { ... this.pageConfig.tableConfig, apiData: response.map((item: any) => ({ ...item, isExpanded: false })) }; }
          if (!this.pageConfig.tableConfig && this.pageConfig.formConfig && response.length) { this.editAction(response[0]); }
        }
      });
    }
  }

  private prepareForm(data: any = {}): void {
    if (!this.pageConfig?.formConfig) return;
    this.dataForm = this.prepareFormGroup(this.pageConfig.formConfig, data);
  }

  public prepareFormGroup(formConfig: FormConfig, data: any): FormGroup {
    const currentForm = this.formBuilder.group({});
    currentForm.addControl(FormFields.Id, new FormControl(0));

    if (formConfig && formConfig.fieldMapping && formConfig.fieldMapping.length > 0) {
      formConfig.fieldMapping.forEach((field) => {
        if (field && field.key) { currentForm.addControl(field.key, new FormControl("", !field.isHidden ? this.prepareFormValidation(field) : null)); }
      });
    }

    if (formConfig && formConfig.subForms && formConfig.subForms.length > 0) {
      formConfig.subForms.forEach(subForm => {
        if (subForm && subForm.subFormDataKey && subForm.fieldMapping && subForm.fieldMapping.length > 0) {
          const subFormGroup: FormGroup[] = [];
          if (data[subForm.subFormDataKey]) { data[subForm.subFormDataKey].forEach((subData: any) => subFormGroup.push(this.prepareFormGroup(subForm, subData))); }
          else { subFormGroup.push(this.prepareFormGroup(subForm, {})); }
          currentForm.addControl(subForm.subFormDataKey, this.formBuilder.array(subFormGroup));
        }
      });
    }
    currentForm.patchValue(data);
    return currentForm;
  }

  private prepareFormValidation(field: FieldMapping): ValidatorFn | null {
    let composedValidations: ValidatorFn | null = Validators.compose([]);
    if (field.validation && field.validation.length) {
      const validList: any[] = [];
      field.validation.forEach((validation) => {
        switch (validation.type) {
          case ValidatorNames.Required: {
            validList.push(Validators.required);
            validation.message = validation.message ? validation.message : `${field.label} is required`;
            break;
          }
          case ValidatorNames.Email: {
            validList.push(Validators.email);
            validation.message = validation.message ? validation.message : `Enter valid email address`;
            break;
          }
        }
      });
      composedValidations = Validators.compose(validList);
    }
    return composedValidations;
  }

  private prepareOptions() {
    this.options = [];
    this.storeOptions(this.pageConfig?.tableConfig?.columnMapping);
    this.storeOptions(this.pageConfig?.formConfig?.fieldMapping);
    this.pageConfig?.tableConfig?.subTables?.forEach(subTable => this.storeOptions(subTable.columnMapping));
    this.pageConfig?.formConfig?.subForms?.forEach(subForm => this.storeOptions(subForm.fieldMapping));
    if (this.options && this.options.length) {
      this.dataService.postData(`${Modules.Option}`, this.options).then((response: any) => {
        if (response) {
          this.options = response;
          if (this.pageConfig.formConfig) { this.pageConfig.formConfig.fieldMapping = this.retriveOptions(this.pageConfig.formConfig.fieldMapping) ?? []; }
          if (this.pageConfig.tableConfig) { this.pageConfig.tableConfig.columnMapping = this.retriveOptions(this.pageConfig.tableConfig.columnMapping) ?? []; }
          this.pageConfig?.formConfig?.subForms?.forEach(subForm => { subForm.fieldMapping = this.retriveOptions(subForm.fieldMapping) ?? [] });
          this.pageConfig?.tableConfig?.subTables?.forEach(subTable => { subTable.columnMapping = this.retriveOptions(subTable.columnMapping) ?? [] });
        }
      });
    }
  }

  private storeOptions(configList: any[] | undefined) {
    if (configList) {
      configList.filter((config: any) => config.optionsTransfer).forEach((subCon: any) => {
        const optionTrans = subCon.optionsTransfer;
        if (!this.options.some(opt => (opt.table && opt.table === optionTrans.table && opt.key === optionTrans.key && opt.value === optionTrans.value)
          || (opt.keyGroup && opt.keyGroup === optionTrans.keyGroup))) {
          this.options.push(optionTrans);
        }
      });
    }
  }

  private retriveOptions(configList: any[] | undefined) {
    if (configList) {
      configList.filter((config: any) => config.optionsTransfer).forEach((subCon: any) => {
        const found = this.options.find(opt =>
          (opt.table && opt.table === subCon.optionsTransfer?.table && opt.key === subCon.optionsTransfer?.key && opt.value === subCon.optionsTransfer?.value) ||
          (opt.keyGroup && opt.keyGroup == subCon.optionsTransfer.keyGroup)
        );
        if (found) { subCon.options = found.options; }
      });
    }
    return configList;
  }

  public actionEvent(action: ActionRequest) {
    if (action.event === ActionEvent.Edit) { this.editAction(action.data); }
    if (action.event === ActionEvent.Delete) { this.deleteAction(action.data); }

    if (action.event === ActionEvent.Save) { this.saveAction(); }
    if (action.event === ActionEvent.Clear) { this.clearAction(); }
    if (action.event === ActionEvent.Cancel) { this.cancelAction(); }
    if (action.event === ActionEvent.AddSub) { this.addSubAction(action.data); }
    if (action.event === ActionEvent.RemoveSub) { this.removeSubAction(action.data); }

    this.actionSubject.next(action);
  }

  private addSubAction(data: any) {
    const formArray = this.dataForm.get(data) as FormArray;
    const formConfig = this.pageConfig.formConfig?.subForms?.find(d => d.subFormDataKey === data);
    if (formConfig) {
      formArray.push(this.prepareFormGroup(formConfig, {}));
    }
  }

  private removeSubAction(data: KeyValuePair) {
    const formArray = this.dataForm.get(data.key) as FormArray;
    if (formArray && formArray.length > 0) { formArray.removeAt(data.value); }
  }

  private clearAction() {
    const resetValues: any = {};
    Object.keys(this.dataForm.controls).forEach(key => resetValues[key] = key === FormFields.Id ? 0 : '');
    this.dataForm.patchValue(resetValues);
    this.isFormSubmitted = false;
  }

  public cancelAction() {
    this.isFormVisible = false;
  }

  public createAction() {
    this.isFormSubmitted = false;
    this.prepareForm();
    this.isFormVisible = true;
  }

  private editAction(data: any) {
    this.isFormSubmitted = false;
    this.prepareForm(data);
    this.isFormVisible = true;
  }

  private saveAction() {
    this.isFormSubmitted = true;
    if (this.dataForm.valid) {
      this.dataService.post(`${Modules.Base}${this.pageConfig.apiUrl}`, this.dataForm.value).then((response: ApiResponse<any>) => {
        if (response.status === StatusFlags.Success) {
          if(this.pageConfig.pageTitle == "Personal" || this.pageConfig.pageTitle == "Education"  || this.pageConfig.pageTitle == "Bank") {
            this.dataService.notify.next({ key: eMessageType.Success, value: `The approval request has been forwarded to HR for review.` });
          } else {
            this.dataService.notify.next({ key: eMessageType.Success, value: `${this.pageConfig.pageTitle} saved successfully` });
          }
          this.prepareTable();
          this.isFormVisible = false;
        }
        else if (response.status === StatusFlags.AlreadyExists) {
          this.dataService.notify.next({ key: eMessageType.Warning, value: `${this.pageConfig.pageTitle} already exists` });
        } else {
          this.dataService.notify.next({ key: eMessageType.Error, value: `Something went wrong` });
        }
      });
    }
  }

  private deleteAction(data: any) {
    this.alertService.confirm(`Are you sure you want to delete ${this.pageConfig.pageTitle}`, '', 'question').then((result) => {
      if (result.isConfirmed) {
        this.dataService.delete(`${Modules.Base}${this.pageConfig.apiUrl}`, data.id).then((response: ApiResponse<any>) => {
          if (response.status === StatusFlags.Success) {
            this.dataService.notify.next({ key: eMessageType.Success, value: `${this.pageConfig.pageTitle} deleted successfully` });
            this.prepareTable();
            this.isFormVisible = false;
          }
        });
      }
    })
  }
}
